import React, { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useSelector } from 'react-redux'
import {
	Box,
	Title,
	TextInput,
	Button,
	Group,
	Text,
	Anchor,
	Input,
	useMantineTheme,
} from '@mantine/core'
import PhoneInput, { isValidPhoneNumber } from 'react-phone-number-input'
import {
	getCheckInById,
	updateCheckIn,
	createCheckIn,
	getExperienceCustomFields,
} from '../services/actions/checkInActions'
import {
	getStationOptions,
	addStationOption,
	getSegmentOptions,
	addSegmentOption,
} from '../services/actions/experienceActions'
import LookupSelect from '../components/LookupSelect'
import LegacyPurchasedItemsModal from '../components/LegacyPurchasedItemsModal'
import WarningsModal from '../components/WarningsModal'
import { getWarningResolutionHref } from './warningUtils'

const getCheckInCustomFields = (checkIn) => {
	const customFields = checkIn?.customFields
	if (
		!customFields ||
		typeof customFields !== 'object' ||
		Array.isArray(customFields)
	) {
		return {}
	}
	return customFields
}

const formatCustomFieldInputValue = (value) => {
	if (value === null || value === undefined) return ''
	if (typeof value === 'string') return value
	if (typeof value === 'number' || typeof value === 'boolean') {
		return String(value)
	}

	try {
		return JSON.stringify(value)
	} catch {
		return String(value)
	}
}

const summaryPanelStyles = (theme) => ({
	backgroundColor:
		theme.colorScheme === 'dark' ? theme.colors.dark[6] : theme.colors.gray[0],
	borderRadius: theme.radius.md,
	border: `1px solid ${
		theme.colorScheme === 'dark' ? theme.colors.dark[4] : theme.colors.gray[3]
	}`,
})

const EditCheckInPage = () => {
	const { id, experienceSlug, orgSlug } = useParams()
	const isCreating = id === 'new'
	const navigate = useNavigate()
	const theme = useMantineTheme()

	const currentUser = useSelector((state) => state.user)
	const [checkIn, setCheckIn] = useState(null)
	const [error, setError] = useState('')
	const [saving, setSaving] = useState(false)
	const [showItemModal, setShowItemModal] = useState(false)
	const [showWarningModal, setShowWarningModal] = useState(false)
	const [showExtras, setShowExtras] = useState(false)
	const [defaultStation, setDefaultStation] = useState(null)
	const [availableCustomFields, setAvailableCustomFields] = useState([])
	const isPhoneValid =
		!checkIn?.travelerPhone || isValidPhoneNumber(checkIn.travelerPhone)
	const checkInCustomFields = getCheckInCustomFields(checkIn)
	const customFieldEntries = [
		...new Set([
			...availableCustomFields,
			...Object.keys(checkInCustomFields),
		]),
	].map((field) => [field, checkInCustomFields[field]])

	useEffect(() => {
		getStationOptions(experienceSlug, orgSlug)
			.then((options) => {
				if (options.length > 0) {
					setDefaultStation(options[0])
				}
			})
			.catch((err) => console.error('Failed to fetch station options:', err))
	}, [experienceSlug, orgSlug])

	useEffect(() => {
		let cancelled = false

		getExperienceCustomFields(experienceSlug, orgSlug)
			.then((fields) => {
				if (cancelled) return
				setAvailableCustomFields(
					Array.isArray(fields)
						? fields.filter(
								(field) => typeof field === 'string' && field.trim(),
							)
						: [],
				)
			})
			.catch((err) => {
				console.error('Failed to fetch custom fields:', err)
				if (!cancelled) setAvailableCustomFields([])
			})

		return () => {
			cancelled = true
		}
	}, [experienceSlug, orgSlug])

	useEffect(() => {
		if (isCreating) {
			setCheckIn({
				travelerName: '',
				travelerEmail: '',
				travelerPhone: '',
				accountName: '',
				station: { id: '', name: '' },
				segment: null,
				bookingNumber: '',
				scheduledCheckInDate: null,
				checkOutDate: '',
				purchasedItems: [],
				warnings: [],
				customFields: {},
				checkInStatus: false,
			})
			return
		}

		getCheckInById(orgSlug, id)
			.then((data) => setCheckIn(data))
			.catch((err) => {
				console.error('Failed to fetch check-in:', err)
				setError('Failed to load check-in')
			})
	}, [defaultStation, id, isCreating, orgSlug])

	const handleSave = async () => {
		if (!checkIn) return

		const { travelerName } = checkIn

		if (!travelerName) {
			alert('Please fill in the required field: Traveler Name.')
			return
		}
		if (!isPhoneValid) {
			alert('Please enter a valid Traveler Phone or clear the field.')
			return
		}

		const payload = {
			...checkIn,
			travelerEmail: checkIn.travelerEmail?.trim() || null,
			stationId: checkIn.station?.id || null,
			segmentId: checkIn.segment?.id || null,
			scheduledCheckInDate: checkIn.scheduledCheckInDate
				? new Date(checkIn.scheduledCheckInDate)
				: null,
			checkOutDate: checkIn.checkOutDate
				? new Date(checkIn.checkOutDate)
				: null,
			purchasedItems: (checkIn.purchasedItems || []).map((item) => ({
				id: item.id,
				bookingItemId: item.bookingItemId,
				bookingId: item.bookingId,
				orderId: item.orderId,
				transactionId: item.transactionId,
				source: item.source || (item.bookingItemId ? 'livehq_on_site_sale' : 'manual'),
				paymentStatus: item.paymentStatus,
				issued: item.issued ?? false,
				paymentMethod: item.paymentMethod || '',
				paymentAmount: item.paymentAmount || '',
				paymentAmountCents: item.paymentAmountCents,
				addedByUser:
					item.paymentMethod && item.paymentMethod !== 'None'
						? currentUser?.authUser?.id || ''
						: '',
			})),
		}

		setSaving(true)
		try {
			if (isCreating) {
				const newCheckIn = await createCheckIn(experienceSlug, orgSlug, payload)
				navigate(
					`/${orgSlug}/experiences/${experienceSlug}/checkIn/${newCheckIn?.ids[0]}`
				)
			} else {
				await updateCheckIn(orgSlug, id, payload)
				navigate(`/${orgSlug}/experiences/${experienceSlug}/manage/checkins`)
			}
		} catch (err) {
			console.error('Failed to save check-in:', err)
			setError('Failed to save changes')
		} finally {
			setSaving(false)
		}
	}

	const handleChange = (field, value) => {
		setCheckIn((prev) => ({ ...prev, [field]: value }))
	}

	const handleCustomFieldChange = (field, value) => {
		setCheckIn((prev) => ({
			...prev,
			customFields: {
				...getCheckInCustomFields(prev),
				[field]: value,
			},
		}))
	}

	const fetchStationOptions = async (input) => {
		const results = await getStationOptions(experienceSlug, orgSlug)
		if (!input) return results
		return results.filter((opt) =>
			opt.name.toLowerCase().includes(input.toLowerCase())
		)
	}

	const createStationOption = async (label) => {
		const newOption = await addStationOption(experienceSlug, orgSlug, {
			name: label,
		})
		return newOption
	}

	const fetchSegmentOptions = async (input) => {
		const results = await getSegmentOptions(experienceSlug, orgSlug)
		if (!input) return results
		return results.filter((opt) =>
			opt.name.toLowerCase().includes(input.toLowerCase())
		)
	}

	const createSegmentOption = async (label) => {
		return addSegmentOption(experienceSlug, orgSlug, { name: label })
	}

	const handleAddWarning = () => {
		setShowWarningModal(true)
	}

	const handleSaveWarning = (warningsArray) => {
		setCheckIn((prev) => ({
			...prev,
			warnings: warningsArray,
		}))
	}

	if (error) return <Text color='red'>{error}</Text>
	if (!checkIn) return <Text>Loading...</Text>

	return (
		<Box px='md' py='md'>
			<Title order={2} mb='md'>
				{isCreating ? 'Add Check-In' : 'Edit Check-In'}
			</Title>

			<TextInput
				label='Traveler Name'
				required
				value={checkIn.travelerName}
				onChange={(e) => handleChange('travelerName', e.target.value)}
				mb='sm'
			/>

			<TextInput
				label='Traveler Email'
				value={checkIn.travelerEmail || ''}
				onChange={(e) => handleChange('travelerEmail', e.target.value)}
				mb='sm'
			/>

			<Input.Wrapper label='Traveler Phone' mb='sm'>
				<PhoneInput
					defaultCountry='US'
					international
					countryCallingCodeEditable={false}
					value={checkIn.travelerPhone || undefined}
					onChange={(value) => handleChange('travelerPhone', value || '')}
					className='phone-input'
					style={{
						'--phone-input-background':
							theme.colorScheme === 'dark'
								? theme.colors.dark[6]
								: theme.white,
						'--phone-input-border':
							theme.colorScheme === 'dark'
								? theme.colors.dark[4]
								: theme.colors.gray[4],
						'--phone-input-color':
							theme.colorScheme === 'dark'
								? theme.colors.dark[0]
								: theme.black,
						'--phone-input-color-scheme': theme.colorScheme,
					}}
				/>
			</Input.Wrapper>

			<Box
				mt='xl'
				px='md'
				py='md'
				sx={summaryPanelStyles}
			>
				<Group position='apart' mb='sm'>
					<Title order={4} m={0}>
						Purchased Items
					</Title>
					<Button
						variant='subtle'
						size='xs'
						onClick={() => setShowItemModal(true)}
					>
						Add / Edit
					</Button>
				</Group>

				{checkIn.purchasedItems?.length > 0 ? (
					<ul style={{ margin: 0, paddingLeft: '1rem' }}>
						{checkIn.purchasedItems.map((item) => (
							<li key={item.id}>
								<Text size='sm'>
									{item.name}{' '}
									{item.paymentMethod && item.paymentMethod !== 'None'
										? `(Paid $${item.paymentAmount || 0} via ${
												item.paymentMethod
										  })`
										: ''}
								</Text>
							</li>
						))}
					</ul>
				) : (
					<Text size='sm' color='dimmed'>
						None selected
					</Text>
				)}
			</Box>

			<Anchor
				mt='md'
				size='sm'
				onClick={() => setShowExtras((prev) => !prev)}
				style={{ display: 'inline-block', cursor: 'pointer' }}
			>
				{showExtras ? 'Hide Additional Fields' : 'Additional Fields'}
			</Anchor>

			{showExtras && (
				<>
					<TextInput
						label='Account Name'
						value={checkIn.accountName}
						onChange={(e) => handleChange('accountName', e.target.value)}
						mb='sm'
					/>

					<TextInput
						label='Check-In Date'
						type='date'
						value={checkIn.scheduledCheckInDate?.slice(0, 10) || ''}
						onChange={(e) =>
							handleChange('scheduledCheckInDate', e.target.value)
						}
						mb='sm'
					/>

					<TextInput
						label='Check-Out Date'
						type='date'
						value={checkIn.checkOutDate?.slice(0, 10) || ''}
						onChange={(e) => handleChange('checkOutDate', e.target.value)}
						mb='sm'
					/>

					<LookupSelect
						label='Station'
						value={
							checkIn.station
								? { id: checkIn.station.id, name: checkIn.station.name }
								: null
						}
						onChange={(opt) => {
							handleChange('station', opt ? { id: opt.id, name: opt.name } : '')
						}}
						fetchOptions={fetchStationOptions}
						onCreateNew={createStationOption}
						getOptionLabel={(opt) => opt.name}
						getOptionValue={(opt) => opt.id}
						placeholder='Select or create a station'
					/>

					<LookupSelect
						label='Segment'
						value={
							checkIn.segment
								? { id: checkIn.segment.id, name: checkIn.segment.name }
								: null
						}
						onChange={(opt) => {
							handleChange(
								'segment',
								opt ? { id: opt.id, name: opt.name } : null
							)
						}}
						fetchOptions={fetchSegmentOptions}
						onCreateNew={createSegmentOption}
						getOptionLabel={(opt) => opt.name}
						getOptionValue={(opt) => opt.id}
						placeholder='Select or create a segment'
					/>

					<TextInput
						label='Booking Number'
						value={checkIn.bookingNumber}
						onChange={(e) => handleChange('bookingNumber', e.target.value)}
						mb='sm'
					/>

					<Box
						mt='md'
						px='md'
						py='md'
						sx={summaryPanelStyles}
					>
						<Group position='apart' mb='sm'>
							<Title order={4} m={0}>
								Warnings
							</Title>
							<Button variant='subtle' size='xs' onClick={handleAddWarning}>
								Add / Edit
							</Button>
						</Group>

						{checkIn.warnings?.length > 0 ? (
							<ul style={{ margin: 0, paddingLeft: '1rem' }}>
								{checkIn.warnings.map((w, i) => (
									<li key={i}>
										<Text size='sm'>
											<strong>{w.level}</strong>: {w.title}
										</Text>
										{getWarningResolutionHref(w.resolutionLink) && (
											<Anchor
												href={getWarningResolutionHref(w.resolutionLink)}
												target='_blank'
												rel='noopener noreferrer'
												size='sm'
											>
												Resolution link
											</Anchor>
										)}
									</li>
								))}
							</ul>
						) : (
							<Text size='sm' color='dimmed'>
								No warnings
							</Text>
						)}
					</Box>

					{customFieldEntries.length > 0 && (
						<Box mt='xl'>
							<Title order={4} mb='sm'>
								Custom Fields
							</Title>
							{customFieldEntries.map(([field, value]) => (
								<TextInput
									key={field}
									label={field}
									value={formatCustomFieldInputValue(value)}
									onChange={(event) =>
										handleCustomFieldChange(field, event.target.value)
									}
									mb='sm'
								/>
							))}
						</Box>
					)}
				</>
			)}

			<Group mt='xl'>
				<Button onClick={handleSave} loading={saving}>
					{isCreating ? 'Create Check-In' : 'Save Changes'}
				</Button>
				<Button
					variant='light'
					color='gray'
					onClick={() =>
						navigate(
							`/${orgSlug}/experiences/${experienceSlug}/manage/checkins`
						)
					}
				>
					Cancel
				</Button>
			</Group>

			<WarningsModal
				opened={showWarningModal}
				onClose={() => setShowWarningModal(false)}
				initial={checkIn.warnings}
				onSave={handleSaveWarning}
			/>

			<LegacyPurchasedItemsModal
				show={showItemModal}
				onClose={() => setShowItemModal(false)}
				experienceSlug={experienceSlug}
				currentItems={checkIn.purchasedItems}
				currentUserId={currentUser?.authUser?.id}
				onSave={(newItems) =>
					setCheckIn((prev) => {
						const merged = newItems.map((item) => ({
							...item,
							issued: item.issued ?? false,
						}))
						return { ...prev, purchasedItems: merged }
					})
				}
			/>
		</Box>
	)
}

export default EditCheckInPage
